import type { ConsiderationsIndustry } from '~kui/components/KUI/Unified/Considerations/KUIUnifiedConsiderationsSection.vue';
import type { ConsiderationRiskStatus } from '~kui/utils/considerationRiskStatus';

import { useQuery } from '@urql/vue';

import { considerationAreaFragment, fetchConsiderationAreas } from '~/graphql/documents/consideration';
import { useFragment } from '~/graphql/generated';
import { toConsiderationColor } from '~/utils/considerationAreaColor';
import { toConsiderationRiskStatus } from '~/utils/considerationRiskStatus';

/** One consideration area as a progress card, keyed so lists can track it; `hubSlug` is set only for an area with an industry hub. */
export type ConsiderationAreaCard = ConsiderationsIndustry & { id: string; hubSlug: string | null };

export const useConsiderationAreas = (pause: Ref<boolean> = ref(false)) => {
  const { user } = useAuth();

  const query = useQuery({
    query: fetchConsiderationAreas,
    variables: {},
    requestPolicy: 'cache-and-network',
    pause,
  });

  const { data, fetching, error, executeQuery } = query;

  const considerationAreas = computed(() =>
    (data.value?.allConsiderationAreas ?? []).map(item => useFragment(considerationAreaFragment, item)),
  );

  const areaCards = computed<ConsiderationAreaCard[]>(() =>
    considerationAreas.value.map(area => ({
      id: area.id,
      // Only an industry-linked area has a hub behind /career-advice/:slug.
      hubSlug: area.industryId && area.slug ? area.slug : null,
      title: area.label,
      color: toConsiderationColor(area.color),
      level: area.userLevel ?? 0,
      illustration: area.iconFile?.publicUrl ?? undefined,
      progress: area.userProgressPercentage ?? 0,
    })),
  );

  const considerationScore = computed(() => user.value?.considerationScore);

  const considerationScorePercentage = computed(() => Math.round(considerationScore.value?.percentage ?? 0));

  const considerationRiskStatus = computed(() => toConsiderationRiskStatus(considerationScore.value?.scoreRisk));

  return {
    considerationAreas,
    areaCards,
    considerationScorePercentage,
    considerationRiskStatus,
    fetching,
    error,
    /** Awaitable @urql/vue query handle — `await` it in setup so SSR-rendered, query-derived HTML matches the hydrated client. */
    query,
    /** Re-execute the query; pass `{ requestPolicy: 'network-only' }` to force a fresh read (e.g. after a completion). */
    refetch: executeQuery,
  };
};

export type { ConsiderationRiskStatus };
